
import React, { useState } from "react";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, Users } from "lucide-react";
import { GroupModal } from "@/components/groups/GroupModal";
import { GroupDeleteDialog } from "@/components/groups/GroupDeleteDialog";
import { useGroups } from "@/hooks/useGroups";
import { GroupFormValues } from "@/components/groups/GroupForm";
import { ContractGroup } from "@/lib/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";

export default function GroupsPage() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ContractGroup | null>(null);

  const {
    useGroupsQuery,
    useDeleteGroupMutation
  } = useGroups();

  const { data: groups = [], isLoading } = useGroupsQuery();
  const deleteGroupMutation = useDeleteGroupMutation();

  const handleGroupCreated = (group: ContractGroup) => {
    setCreateModalOpen(false);
  };

  const handleGroupUpdated = (group: ContractGroup) => {
    setEditModalOpen(false);
    setSelectedGroup(null);
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroupMutation.mutateAsync(groupId);
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error("Failed to delete group:", error);
    }
  };

  const openEditModal = (group: ContractGroup) => {
    setSelectedGroup(group);
    setEditModalOpen(true);
  };

  const openDeleteDialog = (group: ContractGroup) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  return (
    <Shell className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Groups</h1>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Group
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <Skeleton className="h-12 w-full" />
            </div>
          ))}
        </div>
      ) : groups && groups.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{group.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {group.description || (
                      <span className="text-muted-foreground italic">No description</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {format(new Date(group.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(group)}
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(group)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <Users className="h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No groups found</h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            You haven't created any groups yet. Groups help you organize your contracts.
          </p>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create your first group
          </Button>
        </div>
      )}

      <GroupModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={handleGroupCreated}
      />

      {selectedGroup && (
        <GroupModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          group={selectedGroup}
          onSuccess={handleGroupUpdated}
        />
      )}

      <GroupDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        group={selectedGroup}
        onDelete={handleDeleteGroup}
        isPending={deleteGroupMutation.isPending}
      />
    </Shell>
  );
}
